import { CSSProperties } from 'react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

export type SkeletonVariant = 'row' | 'card' | 'table';

export type SkeletonProps = {
  variant?: SkeletonVariant;
  rows?: number;
  className?: string;
};

export type SkeletonBarProps = {
  className?: string;
  style?: CSSProperties;
};

// Deterministic width patterns per table row to look natural
const TABLE_PATTERNS: string[][] = [
  ['flex-1', 'w-1/6', 'w-1/4', 'w-8'],
  ['w-2/5', 'w-1/5', 'flex-1', 'w-8'],
  ['flex-1', 'w-1/4', 'w-1/5', 'w-8'],
  ['w-1/2', 'w-1/6', 'w-1/4', 'w-8'],
  ['w-1/3', 'w-1/5', 'flex-1', 'w-8']
];

/** Low-level building block — export for custom skeleton compositions. */
export function SkeletonBar({ className = '', style }: SkeletonBarProps) {
  const { getColour } = useThemeContext();
  return (
    <div
      className={`animate-pulse rounded-full ${className}`}
      style={{ backgroundColor: getColour('hairline'), ...style }}
    />
  );
}

/** High-level skeleton placeholder. variant controls the shape. */
export function Skeleton({
  variant = 'row',
  rows = 5,
  className = ''
}: SkeletonProps) {
  const { getColour } = useThemeContext();
  const bar: CSSProperties = { backgroundColor: getColour('hairline') };

  if (variant === 'row') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div className="animate-pulse rounded-full h-4 flex-1" style={bar} />
        <div className="animate-pulse rounded-full h-4 w-1/4" style={bar} />
        <div className="animate-pulse rounded-full h-4 w-16" style={bar} />
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`flex flex-col gap-3 ${className}`}>
        <div className="animate-pulse rounded-full h-5 w-3/4" style={bar} />
        <div className="animate-pulse rounded-full h-4 w-1/2" style={bar} />
        <div className="mt-2 flex flex-col gap-2">
          <div className="animate-pulse rounded-full h-3 w-full" style={bar} />
          <div className="animate-pulse rounded-full h-3 w-5/6" style={bar} />
          <div className="animate-pulse rounded-full h-3 w-4/5" style={bar} />
        </div>
      </div>
    );
  }

  // variant === 'table'
  return (
    <div
      className={
        'rounded-2xl border border-hairline overflow-hidden ' + className
      }
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-4 py-3"
          style={
            i > 0
              ? { borderTop: `1px solid ${getColour('hairline')}` }
              : undefined
          }
        >
          {TABLE_PATTERNS[i % TABLE_PATTERNS.length].map((w, j) => (
            <div
              key={j}
              className={`animate-pulse rounded-full h-4 ${w}`}
              style={bar}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
